<template>
    <el-dialog 
      title="查看流转合同" 
      :visible.sync="dialogVisible" 
      width="60%" 
      :before-close="handleClose"
      class="contract-viewer-dialog"
      append-to-body
    >
      <!-- 切换不同合同 -->
      <div class="tabs">
        <div 
          v-for="(item, index) in tabsList" 
          :key="index" 
          :class="{ 'itemCls': index === tabsIndex, 'tab-item': true }"
          @click="tabsClick(index)"
        >
          {{ item.title || `合同${index + 1}` }}
          <span class="count" v-if="item.count > 0">({{ item.count }})</span>
        </div>
      </div>

      <!-- 当前选中合同的基本信息 -->
      <div class="contract-info" v-if="currentContract">
        <h3>合同信息</h3>
        <div class="info-row">
          <span class="label">合同编号：</span>
          <span class="value">{{ currentContract.contractCode || '-' }}</span>
          <span class="label">上传时间：</span>
          <span class="value">{{ currentContract.uploadTime || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="label">上传人：</span>
          <span class="value">{{ currentContract.uploader || '-' }}</span>
          <span class="label">项目名称：</span>
          <span class="value">{{ currentContract.xmmc || '-' }}</span>
        </div>
      </div>

      <!-- 图片展示区域 -->
      <div class="image-container">
        <div v-if="fileList.length === 0" class="empty-state">
          <i class="el-icon-picture"></i>
          <p>暂无合同图片</p>
        </div>
        
        <div v-else class="image-list">
          <div 
            v-for="(file, index) in fileList" 
            :key="file.uid || index"
            class="image-item"
          >
            <div class="image-wrapper">
              <el-image
                :src="file.url"
                :preview-src-list="previewList"
                :initial-index="index"
                fit="cover"
                class="contract-image"
              >
                <div slot="error" class="image-slot">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </el-image>
              
              <!-- 图片操作按钮 -->
              <div class="image-actions">
                <el-tooltip content="预览" placement="top">
                  <el-button 
                    size="mini" 
                    icon="el-icon-zoom-in" 
                    circle 
                    @click="handlePreview(file, index)"
                  ></el-button>
                </el-tooltip>
              </div>
            </div>
            <div class="image-name">{{ file.name || '未命名' }}</div>
          </div>
        </div>
      </div>

      <!-- 底部操作按钮 -->
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleClose">关闭</el-button>
        <el-button 
          type="primary" 
          icon="el-icon-download"
          @click="handleBatchExport"
          :loading="exportLoading"
          :disabled="fileList.length === 0"
        >
          批量导出
        </el-button>
      </div>
    </el-dialog>
</template>

<script>
import { download } from "@/api/ChargeUp";

export default {
  name: 'TransferContractViewer',
  data() {
    return {
      dialogVisible: false,
      tabsIndex: 0,
      exportLoading: false,
      baseUrl: window.globalUrl.BASE_API,
      
      // 合同列表
      contractList: [],
      
      // 标签页列表
      tabsList: [],
      
      // 当前显示的文件列表
      fileList: []
    };
  },
  
  computed: {
    // 当前选中的合同
    currentContract() {
      return this.contractList[this.tabsIndex] || null;
    },
    
    // 当前图片的预览列表
    previewList() {
      return this.fileList.map(file => file.url).filter(url => url);
    }
  },
  
  mounted() {
    console.log('TransferContractViewer mounted');
  },
  
  methods: {
    /**
     * 初始化组件
     * @param {Array} contractPicList 合同图片列表
     */
    async init(contractPicList) {
      
      // 强制显示弹窗
      this.dialogVisible = true;

      try {
        // 存储合同列表
        this.contractList = contractPicList;
        
        // 生成标签页列表
        this.tabsList = contractPicList.map((contract, index) => {
          // 解析图片数据
          const picData = this.parsePicUrl(contract.picUrl);
          
          return {
            title: contract.contractCode ? `合同${contract.contractCode}` : `合同${index + 1}`,
            count: picData.length,
            contractIndex: index
          };
        });
        
        console.log('生成的标签页列表:', this.tabsList);
        
        // 默认显示第一个有图片的合同
        this.showFirstAvailableContract();
      } catch (error) {
        console.error('初始化合同查看器失败:', error);
        this.$message.error('加载合同数据失败');
      }
    },
    
    /**
     * 解析picUrl字段的JSON数据
     */
    parsePicUrl(picUrl) {
      if (!picUrl) return [];
      
      try {
        const picData = typeof picUrl === 'string' ? JSON.parse(picUrl) : picUrl;
        return Array.isArray(picData) ? picData : [];
      } catch (e) {
        console.error('解析合同图片数据失败:', e);
        return [];
      }
    },
    
    /**
     * 显示第一个有图片的合同
     */
    showFirstAvailableContract() {
      const firstAvailableIndex = this.tabsList.findIndex(tab => tab.count > 0);
      if (firstAvailableIndex !== -1) {
        this.tabsClick(firstAvailableIndex);
      } else if (this.tabsList.length > 0) {
        this.tabsClick(0);
      } else {
        this.fileList = [];
      }
    },
    
    /**
     * 切换合同标签
     */
    tabsClick(index) {
      this.tabsIndex = index;
      const contract = this.contractList[index];
      
      if (contract) {
        // 解析并处理图片数据
        const picData = this.parsePicUrl(contract.picUrl);
        this.fileList = this.processFileList(picData);
      } else {
        this.fileList = [];
      }
    },
    
    /**
     * 处理文件列表，统一格式
     */
    processFileList(files) {
      return files.map((file, index) => ({
        uid: `file_${Date.now()}_${index}`,
        name: file.FilesName || `合同图片${index + 1}`,
        url: file.url2 || (file.imgUrl ? `${this.baseUrl}admin/file/get?ossFilePath=${file.imgUrl}` : ''),
        filePath: file.imgUrl,
        originalData: file
      }));
    },
    
    /**
     * 预览图片
     */
    handlePreview(file, index) {
      // 触发 el-image 的预览功能
      console.log('预览图片:', file);
    },
    
    /**
     * 下载单个文件
     */
    async handleDownload(file) {
      try {
        if (!file.url && !file.filePath) {
          this.$message.error('文件路径不存在');
          return;
        }
        
        const downloadUrl = file.url || `${this.baseUrl}admin/file/get?ossFilePath=${file.filePath}`;
        
        const res = await download({
          ossFilePath: downloadUrl
        });
        
        if (res) {
          this.downloadFile(res, file.name);
          this.$message.success('下载成功');
        }
      } catch (error) {
        console.error('下载失败:', error);
        this.$message.error('下载失败');
      }
    },
    
    /**
     * 批量导出当前合同的所有文件
     */
    async handleBatchExport() {
      if (this.fileList.length === 0) {
        this.$message.warning('当前合同没有文件可导出');
        return;
      }
      
      this.exportLoading = true;
      
      try {
        const currentContractCode = this.currentContract?.contractCode || `合同${this.tabsIndex + 1}`;
        
        // 创建一个 Promise 数组来并行下载所有文件
        const downloadPromises = this.fileList.map(async (file, index) => {
          try {
            const downloadUrl = file.url || `${this.baseUrl}admin/file/get?ossFilePath=${file.filePath}`;
            const res = await download({ ossFilePath: downloadUrl });
            return {
              blob: res,
              name: `${currentContractCode}_${index + 1}_${file.name}`,
              success: true
            };
          } catch (error) {
            console.error(`下载文件 ${file.name} 失败:`, error);
            return {
              blob: null,
              name: file.name,
              success: false
            };
          }
        });
        
        const results = await Promise.all(downloadPromises);
        
        // 过滤出成功下载的文件
        const successResults = results.filter(result => result.success);
        
        if (successResults.length === 0) {
          this.$message.error('所有文件下载失败');
          return;
        }
        
        // 如果只有一个文件，直接下载
        if (successResults.length === 1) {
          this.downloadFile(successResults[0].blob, successResults[0].name);
        } else {
          // 多个文件时，可以考虑打包下载或者逐个下载
          // 这里采用逐个下载的方式
          successResults.forEach((result, index) => {
            setTimeout(() => {
              this.downloadFile(result.blob, result.name);
            }, index * 500); // 间隔500ms下载，避免浏览器阻止
          });
        }
        
        const failedCount = results.length - successResults.length;
        if (failedCount > 0) {
          this.$message.warning(`${successResults.length}个文件导出成功，${failedCount}个文件导出失败`);
        } else {
          this.$message.success('批量导出成功');
        }
        
      } catch (error) {
        console.error('批量导出失败:', error);
        this.$message.error('批量导出失败');
      } finally {
        this.exportLoading = false;
      }
    },
    
    /**
     * 下载文件的通用方法
     */
    downloadFile(blob, fileName) {
      const downloadElement = document.createElement('a');
      const href = window.URL.createObjectURL(
        new Blob([blob], {
          type: 'application/octet-stream'
        })
      );
      
      downloadElement.href = href;
      downloadElement.download = decodeURIComponent(fileName);
      document.body.appendChild(downloadElement);
      downloadElement.click();
      document.body.removeChild(downloadElement);
      window.URL.revokeObjectURL(href);
    },
    
    /**
     * 关闭弹窗
     */
    handleClose() {
      this.dialogVisible = false;
      this.$emit('closeDialog', false);
    }
  }
};
</script>

<style lang="less" scoped>
.contract-viewer-dialog {
  /deep/ .el-dialog__body {
    padding: 10px 20px;
  }
}

.contract-info {
  background: #f8f9fa;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
  border-left: 4px solid #409eff;
  
  h3 {
    margin: 0 0 10px 0;
    color: #303133;
    font-size: 16px;
  }
  
  .info-row {
    display: flex;
    margin-bottom: 8px;
    
    .label {
      color: #606266;
      margin-right: 8px;
      min-width: 80px;
    }
    
    .value {
      color: #303133;
      margin-right: 30px;
      flex: 1;
    }
  }
}

.tabs {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #e4e7ed;
  
  .tab-item {
    padding: 12px 20px;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    color: #606266;
    transition: all 0.3s;
    
    &:hover {
      color: #409eff;
    }
    
    .count {
      color: #909399;
      font-size: 12px;
    }
    
    &.itemCls {
      color: #409eff;
      border-bottom-color: #409eff;
      
      .count {
        color: #409eff;
      }
    }
  }
}

.image-container {
  min-height: 200px;
  
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #909399;
    
    i {
      font-size: 48px;
      margin-bottom: 10px;
    }
    
    p {
      margin: 0;
      font-size: 14px;
    }
  }
  
  .image-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    
    .image-item {
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      overflow: hidden;
      transition: all 0.3s;
      
      &:hover {
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        
        .image-actions {
          opacity: 1;
        }
      }
      
      .image-wrapper {
        position: relative;
        height: 150px;
        
        .contract-image {
          width: 100%;
          height: 100%;
          
          /deep/ .el-image__inner {
            transition: all 0.3s;
          }
          
          .image-slot {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
            background: #f5f7fa;
            color: #909399;
            
            i {
              font-size: 30px;
            }
          }
        }
        
        .image-actions {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          opacity: 0;
          transition: opacity 0.3s;
          
          .el-button {
            margin: 0 4px;
            background: rgba(255, 255, 255, 0.9);
            border: none;
            
            &:hover {
              background: #409eff;
              color: white;
            }
          }
        }
      }
      
      .image-name {
        padding: 8px 12px;
        font-size: 12px;
        color: #606266;
        background: #fafafa;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

.dialog-footer {
  text-align: right;
  
  .el-button {
    margin-left: 10px;
  }
}
</style> 